import React, {
    createContext,
    type FC,
    type PropsWithChildren,
    use,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    addReleaseNoteVersion,
    createReleaseNoteStateIfMissing,
    patchReleaseNoteState,
    subscribeRecentReleaseNotes,
    subscribeReleaseNoteState,
} from "@domain/api/firebase/releaseNotes";
import { UserContext } from "@domain/context/UserProvider";
import { logger } from "@domain/infrastructure/logger";
import {
    countUnreadReleaseNotes,
    emptyReleaseNoteState,
    getBaselineState,
    getClearAllPatch,
    getMarkAllReadPatch,
    getReleaseNoteFeed,
    isReleaseNoteRead,
    type ReleaseNote,
    type ReleaseNoteState,
} from "@domain/models/ReleaseNotes";

interface ReleaseNotesContextValue {
    feed: ReleaseNote[];
    unreadCount: number;
    isRead: (note: ReleaseNote) => boolean;
    markRead: (version: string) => void;
    clear: (version: string) => void;
    markAllRead: () => void;
    clearAll: () => void;
}

const noop = () => {};

export const ReleaseNotesContext = createContext<ReleaseNotesContextValue>({
    feed: [],
    unreadCount: 0,
    isRead: () => true,
    markRead: noop,
    clear: noop,
    markAllRead: noop,
    clearAll: noop,
});

const logWriteError = (error: Error) => logger.error("release notes state write failed", error);

export const ReleaseNotesProvider: FC<PropsWithChildren> = ({ children }) => {
    const uid = use(UserContext).fireUser?.uid;
    const [notes, setNotes] = useState<ReleaseNote[]>([]);
    // Tagged with the uid so a previous user's state is never shown after switching accounts.
    const [loaded, setLoaded] = useState<{ uid: string; state: ReleaseNoteState | null } | null>(null);
    // undefined = not loaded yet, null = loaded and missing
    const savedState = uid && loaded?.uid === uid ? loaded.state : undefined;

    useEffect(() => {
        if (!uid) {
            return undefined;
        }
        // The feature is optional: a failed read leaves the bell empty instead of breaking the header.
        const onError = (error: Error) => logger.warning("release notes subscription failed", error.message);
        const unsubscribeNotes = subscribeRecentReleaseNotes(setNotes, onError);
        const unsubscribeState = subscribeReleaseNoteState(uid, (state) => setLoaded({ uid, state }), onError);
        return () => {
            unsubscribeNotes();
            unsubscribeState();
        };
    }, [uid]);

    useEffect(() => {
        if (uid && savedState === null && notes.length > 0) {
            createReleaseNoteStateIfMissing(uid, getBaselineState(notes)).catch(logWriteError);
        }
    }, [uid, savedState, notes]);

    // Until the baseline is saved, show what it will be rather than every note as unread.
    const state = useMemo(
        () => savedState ?? (savedState === null ? getBaselineState(notes) : emptyReleaseNoteState),
        [savedState, notes],
    );

    const markRead = useCallback(
        (version: string) => {
            if (uid) {
                addReleaseNoteVersion(uid, "readVersions", version).catch(logWriteError);
            }
        },
        [uid],
    );
    const clear = useCallback(
        (version: string) => {
            if (uid) {
                addReleaseNoteVersion(uid, "clearedVersions", version).catch(logWriteError);
            }
        },
        [uid],
    );
    const markAllRead = useCallback(() => {
        if (uid) {
            patchReleaseNoteState(uid, getMarkAllReadPatch(notes, state)).catch(logWriteError);
        }
    }, [uid, notes, state]);
    const clearAll = useCallback(() => {
        if (uid) {
            patchReleaseNoteState(uid, getClearAllPatch(notes, state)).catch(logWriteError);
        }
    }, [uid, notes, state]);

    const value = useMemo<ReleaseNotesContextValue>(
        () => ({
            feed: savedState === undefined ? [] : getReleaseNoteFeed(notes, state),
            unreadCount: savedState === undefined ? 0 : countUnreadReleaseNotes(notes, state),
            isRead: (note) => isReleaseNoteRead(note, state),
            markRead,
            clear,
            markAllRead,
            clearAll,
        }),
        [notes, state, savedState, markRead, clear, markAllRead, clearAll],
    );

    return <ReleaseNotesContext value={value}>{children}</ReleaseNotesContext>;
};
